import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  lazy,
  Suspense,
  startTransition,
} from 'react';
import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
} from 'react-router-dom';
import Navbar from 'react-bootstrap/Navbar';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Button from 'react-bootstrap/Button';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles.css';

// Critical imports - load immediately
import ErrorBoundary from './ErrorBoundary';
import SafeRenderProvider from './components/SafeRenderProvider';
import DomainRedirect from './components/DomainRedirect';
import DemoRouteGuard from './components/DemoRouteGuard';

// Non-critical lazy imports
const Home = lazy(() => import(/* webpackChunkName: "home" */ './Home'));
const About = lazy(() => import('./About'));
const Contact = lazy(() => import('./Contact'));
const SignUpModal = lazy(() => import('./SignUpModal'));
const LoginModal = lazy(() => import('./LoginModal'));
const Footer = lazy(() => import('./Footer'));

// Import critical preloader
import { preloadCriticalResources } from './utils/criticalPreloader';

// Desktop detection utility - strict to avoid mobile interference
const isDesktop = () =>
  typeof window !== 'undefined' &&
  window.innerWidth > 1024 &&
  !('ontouchstart' in window);

// Keep Firebase imports as needed for auth
import { auth, hasActiveChatAccess } from './firebase';
import { HelmetProvider } from 'react-helmet-async';
import { signOut } from 'firebase/auth';

// Import icons directly - needed for navbar
import {
  FaUserCircle,
  FaCreditCard,
  FaSearch,
  FaCar,
  FaRobot,
  FaChartLine,
  FaExclamationCircle,
  FaSignInAlt,
  FaUserPlus,
  FaClipboardCheck,
  FaTags,
  FaGift,
} from 'react-icons/fa';
import { useTheme } from './ThemeContext';
import { getDoc, doc } from 'firebase/firestore';
import { db } from './firebase';
import { IMAGES } from './imageConfig';
// Defer cookie banner to after initial paint
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => import('./privacy-cookie-banner'));
  } else {
    setTimeout(() => import('./privacy-cookie-banner'), 2000);
  }
}
import ScrollToTop from './ScrollToTop';

// Simple loading component to replace heavy OptimizedLoadingFallback
const SimpleLoader = ({ text = 'Loading...' }) => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
    <div>{text}</div>
  </div>
);

// Optimized lazy loading with chunk names for better bundle splitting
const UserPage = lazy(
  () => import(/* webpackChunkName: "user-page" */ './UserPage')
);
const Blog = lazy(() => import(/* webpackChunkName: "blog" */ './Blog'));
const BlogPost = lazy(
  () => import(/* webpackChunkName: "blog-post" */ './BlogPost')
);
const Garage = lazy(() => import(/* webpackChunkName: "garage" */ './Garage'));
const VehicleComparison = lazy(
  () =>
    import(/* webpackChunkName: "vehicle-comparison" */ './VehicleComparison')
);
const VINSearch = lazy(
  () => import(/* webpackChunkName: "vin-search" */ './VINSearch')
);
const UserAccount = lazy(
  () => import(/* webpackChunkName: "user-account" */ './UserAccount')
);
const Pricing = lazy(
  () => import(/* webpackChunkName: "pricing" */ './Pricing')
);
const FloatingAIChat = lazy(
  () => import(/* webpackChunkName: "ai-chat" */ './FloatingAIChat')
);
const ExitIntentPopup = lazy(
  () =>
    import(/* webpackChunkName: "exit-intent" */ './components/ExitIntentPopup')
);
const VehicleReport = lazy(
  () => import(/* webpackChunkName: "vehicle-report" */ './VehicleReport')
);
const FAQ = lazy(() => import(/* webpackChunkName: "faq" */ './FAQ'));
const MockVehicleReport = lazy(
  () => import(/* webpackChunkName: "mock-report" */ './MockVehicleReport')
);
const PaymentSuccess = lazy(
  () => import(/* webpackChunkName: "payment-success" */ './PaymentSuccess')
);
const MockVehicleComparison = lazy(
  () =>
    import(/* webpackChunkName: "mock-comparison" */ './MockVehicleComparison')
);
const DemoLogin = lazy(
  () => import(/* webpackChunkName: "demo-login" */ './DemoLogin')
);
const SharedReport = lazy(
  () => import(/* webpackChunkName: "shared-report" */ './SharedReport')
);
const TermsOfService = lazy(
  () => import(/* webpackChunkName: "terms" */ './TermsOfService')
);
const PrivacyPolicy = lazy(
  () => import(/* webpackChunkName: "privacy" */ './privacy-policy')
);
const HowItWorks = lazy(
  () => import(/* webpackChunkName: "how-it-works" */ './HowItWorks')
);
const Payment = lazy(
  () => import(/* webpackChunkName: "payment" */ './components/Payment')
);
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const Inspection = lazy(() => import('./Inspection'));
const NMVTISGuide = lazy(
  () => import(/* webpackChunkName: "nmvtis-guide" */ './NMVTISGuide')
);
const NHTSAGuide = lazy(
  () => import(/* webpackChunkName: "nhtsa-guide" */ './NHTSAGuide')
);
const DMVRecordGuide = lazy(
  () => import(/* webpackChunkName: "dmv-record-guide" */ './DMVRecordGuide')
);
const AffiliateProgram = lazy(
  () => import(/* webpackChunkName: "affiliate-program" */ './AffiliateProgram')
);
const BlackBookNADA = lazy(
  () => import(/* webpackChunkName: "black-book-nada" */ './BlackBookNADA')
);
const ClearVin = lazy(
  () => import(/* webpackChunkName: "clearvin" */ './ClearVin')
);
const States = lazy(
  () => import(/* webpackChunkName: "states" */ './State/state')
);
const DynamicStatePage = lazy(
  () =>
    import(
      /* webpackChunkName: "dynamic-state" */ './components/DynamicStatePage'
    )
);
const DynamicCityPage = lazy(
  () =>
    import(
      /* webpackChunkName: "dynamic-city" */ './components/DynamicCityPage'
    )
);
const DynamicBrandPage = lazy(
  () =>
    import(
      /* webpackChunkName: "brand-vin-check" */ './components/DynamicBrandPage'
    )
);

// BlogPost.css moved to BlogPost.js component (already imported there)

/**
 * SmartSingleSegmentRoute
 * Detects if a single-segment URL is a brand VIN check page (ends in -vin-check)
 * or a state page. Routes to DynamicBrandPage or DynamicStatePage accordingly.
 *
 * State pages in Firestore use IDs like "alaska-vin-check", "texas-vin-check".
 * Brand pages use IDs like "toyota", "honda".
 * City pages reference stateSlug as "state-vin-check" format.
 */
const US_STATE_SLUGS = new Set([
  'alabama',
  'alaska',
  'arizona',
  'arkansas',
  'california',
  'colorado',
  'connecticut',
  'delaware',
  'florida',
  'georgia',
  'hawaii',
  'idaho',
  'illinois',
  'indiana',
  'iowa',
  'kansas',
  'kentucky',
  'louisiana',
  'maine',
  'maryland',
  'massachusetts',
  'michigan',
  'minnesota',
  'mississippi',
  'missouri',
  'montana',
  'nebraska',
  'nevada',
  'new-hampshire',
  'new-jersey',
  'new-mexico',
  'new-york',
  'north-carolina',
  'north-dakota',
  'ohio',
  'oklahoma',
  'oregon',
  'pennsylvania',
  'rhode-island',
  'south-carolina',
  'south-dakota',
  'tennessee',
  'texas',
  'utah',
  'vermont',
  'virginia',
  'washington',
  'west-virginia',
  'wisconsin',
  'wyoming',
]);

const BRAND_SLUGS = new Set([
  'toyota',
  'honda',
  'ford',
  'chevrolet',
  'nissan',
  'jeep',
  'hyundai',
  'kia',
  'subaru',
  'bmw',
  'mercedes-benz',
  'gmc',
  'ram',
  'volkswagen',
  'audi',
  'lexus',
  'mazda',
  'tesla',
  'dodge',
  'acura',
  'buick',
  'cadillac',
  'chrysler',
  'volvo',
  'land-rover',
  'porsche',
  'infiniti',
  'mitsubishi',
  'genesis',
  'lincoln',
  'mini',
  'jaguar',
  'alfa-romeo',
  'maserati',
  'rivian',
  'lucid',
]);

const SmartSingleSegmentRoute = () => {
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.stateSlug || '';

  // If URL ends with -vin-check, determine if it's a state or brand
  if (slug.endsWith('-vin-check')) {
    const baseSlug = slug.replace(/-vin-check$/, '');

    // If the base slug is a US state, route to state page
    // (Firestore statePages docs use IDs like "alaska-vin-check")
    if (US_STATE_SLUGS.has(baseSlug)) {
      return (
        <Suspense fallback={<SimpleLoader text="Loading state page..." />}>
          <DynamicStatePage />
        </Suspense>
      );
    }

    // Otherwise it's a brand page (e.g. /toyota-vin-check)
    return (
      <Suspense fallback={<SimpleLoader text="Loading VIN Check..." />}>
        <DynamicBrandPage key={baseSlug} />
      </Suspense>
    );
  }

  // Bare state slug without -vin-check (e.g. /alaska) → redirect to /alaska-vin-check
  if (US_STATE_SLUGS.has(slug)) {
    // Use useEffect-free redirect via Navigate component
    return <Navigate to={`/${slug}-vin-check`} replace />;
  }

  // Bare brand slug without -vin-check (e.g. /honda) → redirect to /honda-vin-check
  if (BRAND_SLUGS.has(slug)) {
    return <Navigate to={`/${slug}-vin-check`} replace />;
  }

  // Otherwise, treat as a generic dynamic page
  return (
    <Suspense fallback={<SimpleLoader text="Loading page..." />}>
      <DynamicStatePage />
    </Suspense>
  );
};

/**
 * Main App Component - Enhanced with proper payment success flow and shared reports
 *
 * Updated: 2025-06-13 09:54:29 by Mikutzu55
 * Added: Modal-based authentication system replacing page-based auth
 * Added: SignUpModal and LoginModal integration
 * Removed: Separate login/signup page routes in favor of modals
 */

// PrivateRoute Component - Updated to show login modal instead of redirecting
const PrivateRoute = ({ children }) => {
  const [user, setUser] = useState(auth.currentUser);
  const [loading, setLoading] = useState(!auth.currentUser);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      setLoading(false);

      // Check if this is a demo user from sessionStorage
      const isDemoUser = sessionStorage.getItem('isDemoUser') === 'true';

      // If user is not authenticated AND not a demo user, show login modal
      if (!currentUser && !isDemoUser && window.openLoginModal) {
        window.openLoginModal();
      }
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  // Allow access if user is authenticated OR if demo user flag is set
  const isDemoUser = sessionStorage.getItem('isDemoUser') === 'true';
  return user || isDemoUser ? children : <Navigate to="/" replace />;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  // Updated auth modal states - replaced single modal with separate modals
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('free');
  const [hasChatAccess, setHasChatAccess] = useState(false);
  const [chatAccessEndTime, setChatAccessEndTime] = useState(null);
  const [vehicleContext, setVehicleContext] = useState(null);

  // Reference to navbar collapse element
  const navbarCollapseRef = useRef(null);
  const navbarTogglerRef = useRef(null);

  // Initialize mobile performance monitoring
  const performanceMonitor = useRef(null);

  // Simplified initialization - minimal performance overhead
  // Track referral code from ?ref= query param — persist in localStorage so it
  // survives page refreshes and is read at checkout time.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref && /^[A-Z0-9]{6,12}$/i.test(ref)) {
      localStorage.setItem('referralCode', ref.toUpperCase());
    }
  }, []);

  useEffect(() => {
    // Only run basic optimizations in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    // Basic preload only
    preloadCriticalResources();

    // STRICT device detection to prevent mobile interference
    const isDesktop = window.innerWidth > 1024 && !('ontouchstart' in window);
    const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;

    if (isDesktop) {
      // Desktop-only optimizations (only for large screens without touch)
      setTimeout(() => {
        const criticalRoutes = ['/about', '/pricing'];
        criticalRoutes.forEach((route) => {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = route;
          document.head.appendChild(link);
        });
      }, 3000); // Delayed more to not interfere

      // Desktop image optimization
      setTimeout(() => {
        const images = document.querySelectorAll('img');
        images.forEach((img) => {
          if (!img.loading) img.loading = 'eager';
          if (!img.decoding) img.decoding = 'async';
        });
      }, 1000);
    } else if (isMobile) {
      // Mobile optimization - PRESERVE ORIGINAL BEHAVIOR
      setTimeout(() => {
        const images = document.querySelectorAll('img');
        images.forEach((img) => {
          if (!img.loading) img.loading = 'lazy';
          if (!img.decoding) img.decoding = 'async';
        });
      }, 2000); // Delayed to prevent interference
    } else {
      // Tablet/medium screens - conservative approach
      setTimeout(() => {
        const images = document.querySelectorAll('img');
        images.forEach((img) => {
          if (!img.loading) img.loading = 'lazy';
          if (!img.decoding) img.decoding = 'async';
        });
      }, 1500);
    }
  }, []);

  // Compute once on mount instead of on every re-render
  const { currentDate, currentTime, currentDateTime } = useMemo(() => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');
    const seconds = String(now.getUTCSeconds()).padStart(2, '0');
    return {
      currentDate: `${year}-${month}-${day}`,
      currentTime: `${hours}:${minutes}:${seconds}`,
      currentDateTime: `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`,
    };
  }, []);
  const currentUser = 'VinSpectorAi';

  // Function to close the mobile navbar when clicking on a link
  const closeNavbar = () => {
    if (
      window.innerWidth < 992 &&
      navbarCollapseRef.current &&
      navbarCollapseRef.current.classList.contains('show')
    ) {
      navbarTogglerRef.current.click();
    }
  };

  // FIXED: Enhanced payment handling without conflicting global redirect
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const paymentSuccess = queryParams.get('payment_success');
    const paymentCanceled = queryParams.get('payment_canceled');

    // REMOVED: Global payment success handler that was conflicting with PaymentSuccess component
    // The /payment-success route now handles success cases properly via PaymentSuccess component

    // Only handle payment success if NOT already on payment-success page
    if (
      paymentSuccess === 'true' &&
      !location.pathname.includes('/payment-success')
    ) {
      console.log(
        '🔄 App.js: Detected payment success, redirecting to PaymentSuccess component'
      );
      navigate('/payment-success' + location.search, { replace: true });
    } else if (paymentCanceled === 'true') {
      // Handle canceled payment
      console.log('💳 App.js: Payment was canceled');

      // Show user-friendly notification for canceled payment
      if (location.pathname === '/pricing') {
        // Already on pricing page, just log it
        console.log('User is on pricing page after cancellation');
      } else {
        // Redirect to pricing page with cancellation notice
        navigate('/pricing?payment_canceled=true');
      }
    }
  }, [location, navigate]);

  // Handle navigation state for showing login modal
  useEffect(() => {
    if (location.state?.showLoginModal) {
      setShowLoginModal(true);
      // Clear the state to prevent modal from showing again on page refresh
      navigate(location.pathname, {
        replace: true,
        state: { ...location.state, showLoginModal: false },
      });
    }
  }, [location.state, navigate]);

  // Keep track of auth state and fetch user data
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        // Fetch user role and chat access status
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserRole(userData.membership || 'free');

            // Get vehicle context for the AI chat
            if (userData.lastSearchedVehicle) {
              setVehicleContext(userData.lastSearchedVehicle);
            }

            // Check if user has chat access
            const isPremium = ['premium', 'business'].includes(
              (userData.membership || '').toLowerCase()
            );
            if (isPremium && userData.lastSearchTime) {
              const lastSearchTime = userData.lastSearchTime.toDate();
              const now = new Date();
              const endTime = new Date(
                lastSearchTime.getTime() + 24 * 60 * 60 * 1000
              );

              if (endTime > now) {
                setHasChatAccess(true);
                setChatAccessEndTime(endTime);
              } else {
                setHasChatAccess(false);
                setChatAccessEndTime(null);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setUserRole('free');
        setHasChatAccess(false);
        setChatAccessEndTime(null);
        setVehicleContext(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Check chat access periodically
  useEffect(() => {
    if (!user) return;

    const checkChatAccess = async () => {
      const hasAccess = await hasActiveChatAccess();
      setHasChatAccess(hasAccess);
    };

    // Check initially and then every minute
    checkChatAccess();
    const interval = setInterval(checkChatAccess, 60000);

    return () => clearInterval(interval);
  }, [user]);

  // Updated auth modal handlers
  const handleAuthModal = (login = true) => {
    startTransition(() => {
      if (login) {
        setShowLoginModal(true);
      } else {
        setShowSignUpModal(true);
      }
    });
  };

  // Expose global functions for opening modals
  useEffect(() => {
    window.openLoginModal = () =>
      startTransition(() => setShowLoginModal(true));
    window.openSignUpModal = () =>
      startTransition(() => setShowSignUpModal(true));

    // Cleanup on unmount
    return () => {
      delete window.openLoginModal;
      delete window.openSignUpModal;
    };
  }, []);

  // New auth success handlers
  const handleSignUpSuccess = (user) => {
    console.log('User signed up successfully:', user);
    // The auth state change will automatically update your user state
    // You can add any additional welcome flows here
  };

  const handleLoginSuccess = (user) => {
    console.log('User logged in successfully:', user);
    // Similar to signup success
  };

  // Modal switching handlers
  const handleSwitchToLogin = () => {
    startTransition(() => {
      setShowSignUpModal(false);
      setShowLoginModal(true);
    });
  };

  const handleSwitchToSignUp = () => {
    startTransition(() => {
      setShowLoginModal(false);
      setShowSignUpModal(true);
    });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Calculate remaining chat time
  const getRemainingChatTime = () => {
    if (!chatAccessEndTime) return null;

    const now = new Date();
    const diffMs = chatAccessEndTime - now;

    if (diffMs <= 0) return null;

    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHrs}h ${diffMins}m`;
  };

  // Provide current date, time, user as global app context
  useEffect(() => {
    window.appContext = {
      currentDate,
      currentTime,
      currentDateTime,
      currentUser,
    };
  }, [currentDate, currentTime, currentDateTime, currentUser]);

  // SPA page view tracking — fires on every route change
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-6M0GP4ZWMZ', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
      });
    }
  }, [location.pathname, location.search]);

  // Scroll depth tracking
  useEffect(() => {
    const scrollDepths = { 25: false, 50: false, 75: false, 90: false };

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage =
        (scrollTop / (documentHeight - windowHeight)) * 100;

      // Track scroll milestones
      Object.keys(scrollDepths).forEach((depth) => {
        const depthNum = parseInt(depth);
        if (scrollPercentage >= depthNum && !scrollDepths[depth]) {
          scrollDepths[depth] = true;

          // Track scroll depth event
          if (window.gtag) {
            window.gtag('event', 'scroll', {
              event_category: 'engagement',
              event_label: `${depth}%`,
              value: depthNum,
            });
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Determine if current user is premium or business
  const isPremium = ['premium', 'business'].includes(userRole.toLowerCase());

  // Enhanced login button style
  const loginButtonStyles = {
    loginButton: {
      display: 'flex',
      alignItems: 'center',
      background:
        theme === 'light'
          ? 'linear-gradient(135deg, rgba(0, 171, 228, 0.9), rgba(51, 191, 233, 0.9))'
          : 'linear-gradient(135deg, rgba(75, 159, 225, 0.8), rgba(129, 212, 250, 0.8))',
      color: 'white',
      borderRadius: '30px',
      padding: '0.5rem 1.25rem',
      border: 'none',
      boxShadow: `0 3px 10px ${theme === 'light' ? 'rgba(0, 171, 228, 0.3)' : 'rgba(129, 212, 250, 0.3)'}`,
      transition: 'all 0.3s ease',
      fontSize: '0.95rem',
      fontWeight: '600',
      position: 'relative',
      overflow: 'hidden',
    },
    loginIcon: {
      marginRight: '8px',
      filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))',
    },
    ripple: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background:
        'radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 70%)',
      opacity: 0.5,
      pointerEvents: 'none',
    },
  };

  return (
    <HelmetProvider>
      <SafeRenderProvider>
        <DomainRedirect>
          <DemoRouteGuard onShowSignUpModal={() => setShowSignUpModal(true)}>
            <ScrollToTop />
            <div
              data-theme={theme}
              className="app-wrapper"
              data-testid="app-loaded"
            >
              {/* Navigation Bar */}
              <Navbar
                bg={theme === 'light' ? 'light' : 'dark'}
                variant={theme === 'light' ? 'light' : 'dark'}
                expand="lg"
                fixed="top"
                className="site-navbar"
              >
                <Container>
                  <Navbar.Brand
                    as={Link}
                    to="/"
                    className="fw-bold d-flex align-items-center"
                  >
                    <img
                      loading="lazy"
                      src={IMAGES.CAR_LOGO}
                      alt="VinSpectorAI Logo"
                      height="60"
                      className="logo-image"
                    />
                  </Navbar.Brand>

                  {/* Auth buttons — placed before Toggle so they stay in the navbar bar on mobile */}
                  {user ? (
                    <div className="d-flex align-items-center ms-auto me-2 me-lg-0 order-lg-last">
                      <NavDropdown
                        title={
                          user.photoURL ? (
                            <img
                              loading="lazy"
                              src={user.photoURL}
                              alt={user.displayName || user.email}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: `2px solid ${theme === 'light' ? 'rgba(0, 171, 228, 0.7)' : 'rgba(129, 212, 250, 0.7)'}`,
                                boxShadow: `0 0 10px ${theme === 'light' ? 'rgba(0, 171, 228, 0.6)' : 'rgba(129, 212, 250, 0.6)'}`,
                                transition:
                                  'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                              }}
                            />
                          ) : (
                            <div className="user-avatar-wrapper">
                              <FaUserCircle
                                size={28}
                                className="user-avatar-icon"
                                style={{
                                  color:
                                    theme === 'light'
                                      ? 'var(--accent-color-light, #00ABE4)'
                                      : 'var(--accent-color-dark, #81d4fa)',
                                  filter: `drop-shadow(0 0 8px ${theme === 'light' ? 'rgba(0, 171, 228, 0.7)' : 'rgba(129, 212, 250, 0.7)'})`,
                                }}
                              />
                            </div>
                          )
                        }
                        id="basic-nav-dropdown"
                        align="end"
                        className={
                          theme === 'light'
                            ? 'text-dark futuristic-dropdown'
                            : 'text-white futuristic-dropdown'
                        }
                      >
                        <div className="dropdown-user-header">
                          <div className="dropdown-user-info">
                            <div className="dropdown-username">
                              {user.displayName || user.email}
                            </div>
                            <div
                              className="dropdown-membership-badge"
                              style={{
                                background: isPremium
                                  ? 'linear-gradient(135deg, #6a11cb, #2575fc)'
                                  : 'linear-gradient(135deg, #718096, #4A5568)',
                                color: '#fff',
                                padding: '3px 8px',
                                borderRadius: '12px',
                                fontSize: '0.7rem',
                                fontWeight: '600',
                                display: 'inline-block',
                                marginTop: '4px',
                                boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)',
                              }}
                            >
                              {isPremium ? 'Premium Member' : 'Free Member'}
                            </div>
                          </div>
                        </div>

                        <NavDropdown.Item
                          as={Link}
                          to="/my-account"
                          onClick={closeNavbar}
                          className="futuristic-dropdown-item"
                        >
                          <span className="dropdown-item-icon">
                            <FaUserCircle />
                          </span>
                          My Account
                        </NavDropdown.Item>

                        <NavDropdown.Item
                          as={Link}
                          to="/garage"
                          onClick={closeNavbar}
                          className="futuristic-dropdown-item"
                        >
                          <span className="dropdown-item-icon">
                            <FaCar />
                          </span>
                          My Garage
                        </NavDropdown.Item>

                        {isPremium && (
                          <NavDropdown.Item
                            as={Link}
                            to="/vin-search"
                            onClick={closeNavbar}
                            className="futuristic-dropdown-item"
                          >
                            <span className="dropdown-item-icon">
                              <FaRobot />
                            </span>
                            AI Search
                          </NavDropdown.Item>
                        )}

                        <NavDropdown.Divider className="futuristic-divider" />

                        <NavDropdown.Item
                          onClick={() => {
                            closeNavbar();
                            handleLogout();
                          }}
                          className="futuristic-dropdown-item logout-item"
                        >
                          <span className="dropdown-item-icon logout-icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 16 16"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0v2z"
                              />
                              <path
                                fillRule="evenodd"
                                d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3z"
                              />
                            </svg>
                          </span>
                          Logout
                        </NavDropdown.Item>
                      </NavDropdown>

                      <Button
                        variant="futuristic"
                        onClick={toggleTheme}
                        aria-label="Toggle theme"
                        className="theme-toggle-btn ms-2"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          position: 'relative',
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <div
                          className="theme-icon-wrapper"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            background:
                              theme === 'light'
                                ? 'linear-gradient(135deg, rgba(0, 171, 228, 0.2), rgba(0, 171, 228, 0.1))'
                                : 'linear-gradient(135deg, rgba(129, 212, 250, 0.2), rgba(129, 212, 250, 0.1))',
                            boxShadow: `0 0 12px ${theme === 'light' ? 'rgba(0, 171, 228, 0.6)' : 'rgba(129, 212, 250, 0.6)'}`,
                            borderRadius: '50%',
                            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: 'rotate(0deg)',
                          }}
                        >
                          {theme === 'light' ? '🌙' : '☀️'}
                        </div>
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center ms-auto me-2 me-lg-0 order-lg-last">
                      {/* Demo Button - Prominent */}
                      <Button
                        variant="success"
                        onClick={() => {
                          closeNavbar();
                          navigate('/demo-login');
                        }}
                        className="demo-nav-btn me-2 d-none d-lg-flex"
                        style={{
                          fontWeight: '600',
                          padding: '8px 16px',
                          fontSize: '0.9rem',
                          background:
                            'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          border: 'none',
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                          transition: 'all 0.3s ease',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow =
                            '0 4px 12px rgba(16, 185, 129, 0.5)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow =
                            '0 2px 8px rgba(16, 185, 129, 0.3)';
                        }}
                      >
                        <FaCar size={14} />
                        <span className="d-none d-lg-inline">Demo</span>
                      </Button>

                      {/* Login Button */}
                      <Button
                        variant="futuristic-login"
                        onClick={() => handleAuthModal(true)}
                        aria-label="Login"
                        className="login-btn me-1 d-none d-lg-flex"
                        style={{
                          alignItems: 'center',
                          background:
                            theme === 'light'
                              ? 'linear-gradient(135deg, rgba(0, 171, 228, 0.9), rgba(51, 191, 233, 0.9))'
                              : 'linear-gradient(135deg, rgba(75, 159, 225, 0.8), rgba(129, 212, 250, 0.8))',
                          color: 'white',
                          borderRadius: '30px',
                          padding: '0.45rem 1rem',
                          border: 'none',
                          boxShadow: `0 3px 10px ${theme === 'light' ? 'rgba(0, 171, 228, 0.3)' : 'rgba(129, 212, 250, 0.3)'}`,
                          transition: 'all 0.3s ease',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          gap: '5px',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = `0 6px 15px ${theme === 'light' ? 'rgba(0, 171, 228, 0.4)' : 'rgba(129, 212, 250, 0.4)'}`;
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = `0 3px 10px ${theme === 'light' ? 'rgba(0, 171, 228, 0.3)' : 'rgba(129, 212, 250, 0.3)'}`;
                        }}
                      >
                        <FaSignInAlt size={13} />
                        <span className="d-none d-lg-inline">Login</span>
                      </Button>

                      {/* Sign Up Button */}
                      <Button
                        variant="futuristic-signup"
                        onClick={() => handleAuthModal(false)}
                        aria-label="Sign Up"
                        className="signup-btn me-2"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          background:
                            theme === 'light'
                              ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                              : 'linear-gradient(135deg, #f5b731, #d97706)',
                          color: 'white',
                          borderRadius: '30px',
                          padding: '0.45rem 1rem',
                          border: 'none',
                          boxShadow: `0 3px 10px ${theme === 'light' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                          transition: 'all 0.3s ease',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          gap: '5px',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow =
                            '0 6px 15px rgba(245, 158, 11, 0.45)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow =
                            '0 3px 10px rgba(245, 158, 11, 0.3)';
                        }}
                      >
                        <FaUserPlus size={13} />
                        <span>Sign Up</span>
                      </Button>

                      <Button
                        variant="futuristic"
                        onClick={toggleTheme}
                        aria-label="Toggle theme"
                        className="theme-toggle-btn"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          position: 'relative',
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <div
                          className="theme-icon-wrapper"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            background:
                              theme === 'light'
                                ? 'linear-gradient(135deg, rgba(0, 171, 228, 0.2), rgba(0, 171, 228, 0.1))'
                                : 'linear-gradient(135deg, rgba(129, 212, 250, 0.2), rgba(129, 212, 250, 0.1))',
                            boxShadow: `0 0 12px ${theme === 'light' ? 'rgba(0, 171, 228, 0.6)' : 'rgba(129, 212, 250, 0.6)'}`,
                            borderRadius: '50%',
                            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: 'rotate(0deg)',
                          }}
                        >
                          {theme === 'light' ? '🌙' : '☀️'}
                        </div>
                      </Button>
                    </div>
                  )}

                  <Navbar.Toggle
                    aria-controls="basic-navbar-nav"
                    ref={navbarTogglerRef}
                  />
                  <Navbar.Collapse
                    id="basic-navbar-nav"
                    ref={navbarCollapseRef}
                  >
                    <Nav className="ms-auto">
                      <Nav.Link
                        as={Link}
                        to="/"
                        className={
                          theme === 'light' ? 'text-dark' : 'text-white'
                        }
                        onClick={closeNavbar}
                      >
                        Home
                      </Nav.Link>
                      <Nav.Link
                        as={Link}
                        to="/how-it-works"
                        className={
                          theme === 'light' ? 'text-dark' : 'text-white'
                        }
                        onClick={closeNavbar}
                      >
                        How It Works
                      </Nav.Link>
                      <Nav.Link
                        as={Link}
                        to="/pricing"
                        className={
                          theme === 'light' ? 'text-dark' : 'text-white'
                        }
                        onClick={closeNavbar}
                      >
                        <FaTags className="me-1" /> Pricing
                      </Nav.Link>
                      <Nav.Link
                        as={Link}
                        to="/blog"
                        className={
                          theme === 'light' ? 'text-dark' : 'text-white'
                        }
                        onClick={closeNavbar}
                      >
                        Blog
                      </Nav.Link>
                      <Nav.Link
                        as={Link}
                        to="/garage"
                        className={
                          theme === 'light' ? 'text-dark' : 'text-white'
                        }
                        onClick={closeNavbar}
                      >
                        <FaCar className="me-1" /> Garage
                      </Nav.Link>
                      <Nav.Link
                        as={Link}
                        to="/vin-search"
                        className={
                          theme === 'light' ? 'text-dark' : 'text-white'
                        }
                        onClick={closeNavbar}
                      >
                        <FaSearch className="me-1" /> VIN Search
                      </Nav.Link>
                      <Nav.Link
                        as={Link}
                        to="/inspection-checklist"
                        className={
                          theme === 'light' ? 'text-dark' : 'text-white'
                        }
                        onClick={closeNavbar}
                      >
                        <FaClipboardCheck className="me-1" /> Inspection Guide
                      </Nav.Link>

                      {/* Refer & Earn — visible to logged-in users */}
                      {user && (
                        <Nav.Link
                          as={Link}
                          to="/my-account?tab=referral"
                          className={
                            theme === 'light' ? 'text-dark' : 'text-white'
                          }
                          onClick={closeNavbar}
                          style={{ fontWeight: 600 }}
                        >
                          <FaGift
                            className="me-1"
                            style={{ color: '#0d6efd' }}
                          />{' '}
                          Refer & Earn
                        </Nav.Link>
                      )}
                      {!user && (
                        <>
                          <hr
                            className="d-lg-none my-2"
                            style={{ opacity: 0.15 }}
                          />
                          <Nav.Link
                            className="d-lg-none"
                            onClick={() => {
                              closeNavbar();
                              handleAuthModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <FaSignInAlt
                              className="me-1"
                              style={{ color: '#00abe4' }}
                            />{' '}
                            Login
                          </Nav.Link>
                          <Nav.Link
                            className="d-lg-none"
                            onClick={() => {
                              closeNavbar();
                              navigate('/demo-login');
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <FaCar
                              className="me-1"
                              style={{ color: '#10b981' }}
                            />{' '}
                            Demo
                          </Nav.Link>
                        </>
                      )}

                      {/* AI Chat Access Indicator for Premium Users */}
                      {user && isPremium && (
                        <NavDropdown
                          title={
                            <span className="d-flex align-items-center">
                              <FaRobot className="me-1" />
                              <span className="d-none d-lg-inline">
                                AI Assistant
                              </span>
                              {hasChatAccess && (
                                <Badge
                                  bg="success"
                                  className="ms-1"
                                  pill
                                  style={{ fontSize: '0.6rem' }}
                                >
                                  Active
                                </Badge>
                              )}
                            </span>
                          }
                          id="ai-dropdown"
                          align="end"
                        >
                          <div
                            className="px-3 py-2"
                            style={{ minWidth: '220px' }}
                          >
                            {hasChatAccess ? (
                              <>
                                <div className="d-flex align-items-center mb-2">
                                  <FaRobot className="text-success me-2" />
                                  <span className="fw-bold">
                                    AI Chat Active
                                  </span>
                                </div>
                                <small className="d-block text-muted mb-2">
                                  Time remaining: {getRemainingChatTime()}
                                </small>
                                <div className="d-grid">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                      closeNavbar();
                                      if (isDesktop()) {
                                        navigate('/garage');
                                      } else {
                                        startTransition(() => {
                                          navigate('/garage');
                                        });
                                      }
                                    }}
                                  >
                                    Start Chatting
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="d-flex align-items-center mb-2">
                                  <FaExclamationCircle className="text-warning me-2" />
                                  <span className="fw-bold">
                                    AI Chat Inactive
                                  </span>
                                </div>
                                <small className="d-block text-muted mb-2">
                                  Search for a vehicle to activate 24-hour AI
                                  chat.
                                </small>
                                <div className="d-grid">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                      closeNavbar();
                                      if (isDesktop()) {
                                        navigate('/vin-search');
                                      } else {
                                        startTransition(() => {
                                          navigate('/vin-search');
                                        });
                                      }
                                    }}
                                  >
                                    Perform Search
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        </NavDropdown>
                      )}
                    </Nav>
                  </Navbar.Collapse>
                </Container>
              </Navbar>

              {/* Add custom CSS for mobile responsiveness */}
              <style>
                {`
          @media (max-width: 767px) {
            .login-btn {
              padding: 0.4rem 1rem !important;
              font-size: 0.9rem !important;
            }
          }

        /* Animation for login button */
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 ${theme === 'light' ? 'rgba(0, 171, 228, 0.5)' : 'rgba(129, 212, 250, 0.5)'};
          }
          70% {
            box-shadow: 0 0 0 8px ${theme === 'light' ? 'rgba(0, 171, 228, 0)' : 'rgba(129, 212, 250, 0)'};
          }
          100% {
            box-shadow: 0 0 0 0 ${theme === 'light' ? 'rgba(0, 171, 228, 0)' : 'rgba(129, 212, 250, 0)'};
          }
        }          .login-btn:focus {
            animation: pulse 1.5s infinite;
          }
        `}
              </style>

              {/* Updated Auth Modals - Replaced single modal with separate modals */}
              <SignUpModal
                show={showSignUpModal}
                onHide={() => startTransition(() => setShowSignUpModal(false))}
                onSuccess={handleSignUpSuccess}
                onSwitchToLogin={handleSwitchToLogin}
              />

              <LoginModal
                show={showLoginModal}
                onHide={() => startTransition(() => setShowLoginModal(false))}
                onSuccess={handleLoginSuccess}
                onSwitchToSignUp={handleSwitchToSignUp}
              />

              {/* Main Content */}
              <div className="page-content">
                <ErrorBoundary
                  fallback={
                    <div>Something went wrong. Please try again later.</div>
                  }
                >
                  <Container className="mt-5 pt-5 main-container">
                    <Suspense fallback={<SimpleLoader />}>
                      <Routes>
                        {/* PUBLIC ROUTES - No authentication required */}
                        <Route path="/" element={<Home theme={theme} />} />
                        <Route
                          path="/how-it-works"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading How It Works..." />
                              }
                            >
                              <HowItWorks theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/about"
                          element={<About theme={theme} />}
                        />
                        <Route
                          path="/contact"
                          element={<Contact theme={theme} />}
                        />
                        {/* REMOVED: Separate login/signup page routes in favor of modals */}
                        {/* <Route path="/login" element={<Login theme={theme} />} />
                <Route path="/signup" element={<SignUp theme={theme} />} /> */}
                        <Route
                          path="/pricing"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading pricing..." />
                              }
                            >
                              <Pricing theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/demo-login"
                          element={
                            <Suspense
                              fallback={<SimpleLoader text="Loading demo..." />}
                            >
                              <DemoLogin />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/terms"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading terms..." />
                              }
                            >
                              <TermsOfService />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/privacy"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading privacy policy..." />
                              }
                            >
                              <PrivacyPolicy />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/payment"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading payment..." />
                              }
                            >
                              <Payment />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/vin-search"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading VIN search..." />
                              }
                            >
                              <VINSearch theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/inspection-checklist"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading inspection..." />
                              }
                            >
                              <Inspection theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/black-book-value-by-vin"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading valuation guide..." />
                              }
                            >
                              <BlackBookNADA theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/clearvin-vehicle-history-reports-vin-check"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading ClearVin guide..." />
                              }
                            >
                              <ClearVin theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/vin-check-by-state"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading states..." />
                              }
                            >
                              <States theme={theme} />
                            </Suspense>
                          }
                        />
                        {/* Dynamic City Page Route - Must come before state route */}
                        <Route
                          path="/:stateSlug/:citySlug"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading city page..." />
                              }
                            >
                              <DynamicCityPage />
                            </Suspense>
                          }
                        />
                        {/* Dynamic State/Brand Route - Catches single-segment slugs */}
                        <Route
                          path="/:stateSlug"
                          element={<SmartSingleSegmentRoute />}
                        />
                        <Route
                          path="/blog"
                          element={
                            <Suspense
                              fallback={<SimpleLoader text="Loading blog..." />}
                            >
                              <Blog theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/blog/:slug"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading article..." />
                              }
                            >
                              <BlogPost theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/blog/new"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading editor..." />
                              }
                            >
                              <BlogPost theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/faq"
                          element={
                            <Suspense
                              fallback={<SimpleLoader text="Loading FAQ..." />}
                            >
                              <FAQ />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/affiliate-program"
                          element={
                            <Suspense
                              fallback={<SimpleLoader text="Loading..." />}
                            >
                              <AffiliateProgram theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/nmvtis-guide"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading NMVTIS guide..." />
                              }
                            >
                              <NMVTISGuide theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/what-is-nhtsa"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading NHTSA guide..." />
                              }
                            >
                              <NHTSAGuide theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/dmv-record-guide"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading DMV record guide..." />
                              }
                            >
                              <DMVRecordGuide theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/demo-report"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading demo report..." />
                              }
                            >
                              <MockVehicleReport theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/demo-vehiclecomparison"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading comparison demo..." />
                              }
                            >
                              <MockVehicleComparison />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/vehicle-comparison"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading vehicle comparison..." />
                              }
                            >
                              <MockVehicleComparison />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/payment-success"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading success page..." />
                              }
                            >
                              <PaymentSuccess theme={theme} />
                            </Suspense>
                          }
                        />

                        {/* ADD THIS LINE: Public Shared Report Route - No authentication required */}
                        <Route
                          path="/shared-report/:shareToken"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading shared report..." />
                              }
                            >
                              <SharedReport />
                            </Suspense>
                          }
                        />

                        {/* Protected Routes */}
                        <Route
                          path="/vehicle-report/:vin"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading vehicle report..." />
                              }
                            >
                              <VehicleReport theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/blog/edit/:slug"
                          element={<BlogPost theme={theme} />}
                        />
                        <Route
                          path="/garage"
                          element={
                            <Suspense
                              fallback={
                                <SimpleLoader text="Loading garage..." />
                              }
                            >
                              <Garage theme={theme} />
                            </Suspense>
                          }
                        />
                        <Route
                          path="/compare"
                          element={
                            <PrivateRoute>
                              <Suspense
                                fallback={
                                  <SimpleLoader text="Loading comparison..." />
                                }
                              >
                                <VehicleComparison theme={theme} />
                              </Suspense>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/my-account"
                          element={
                            <PrivateRoute>
                              <Suspense
                                fallback={
                                  <SimpleLoader text="Loading account..." />
                                }
                              >
                                <UserAccount theme={theme} />
                              </Suspense>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/user-account"
                          element={
                            <PrivateRoute>
                              <Suspense
                                fallback={
                                  <SimpleLoader text="Loading account..." />
                                }
                              >
                                <UserAccount theme={theme} />
                              </Suspense>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/user-page/:make/:model/:year/:registrationStatus/:owner"
                          element={
                            <PrivateRoute>
                              <Suspense
                                fallback={
                                  <SimpleLoader text="Loading user page..." />
                                }
                              >
                                <UserPage theme={theme} />
                              </Suspense>
                            </PrivateRoute>
                          }
                        />
                        <Route
                          path="/admin/dashboard"
                          element={
                            <PrivateRoute>
                              <Suspense
                                fallback={
                                  <SimpleLoader text="Loading admin dashboard..." />
                                }
                              >
                                <AdminDashboardPage theme={theme} />
                              </Suspense>
                            </PrivateRoute>
                          }
                        />

                        {/* Firebase Auth Handler Routes - Handle OAuth callbacks */}
                        <Route
                          path="/__/auth/*"
                          element={
                            <div className="text-center mt-5">
                              <p>Processing authentication...</p>
                              <SimpleLoader text="Authenticating..." />
                            </div>
                          }
                        />

                        {/* 404 Route */}
                        <Route
                          path="*"
                          element={
                            <div className="text-center mt-5 page-min-height">
                              <div
                                style={{
                                  backgroundColor: '#f3f4f6',
                                  padding: '20px',
                                  margin: '20px',
                                  borderRadius: '8px',
                                }}
                              >
                                <h1>404 - Page Not Found</h1>
                                <p
                                  style={{
                                    color: '#4b5563',
                                    marginBottom: '10px',
                                  }}
                                >
                                  The requested page could not be found.
                                </p>
                                <p
                                  style={{ fontSize: '14px', color: '#6b7280' }}
                                >
                                  Current URL:{' '}
                                  <code>{window.location.href}</code>
                                </p>
                                <p
                                  style={{ fontSize: '14px', color: '#6b7280' }}
                                >
                                  Pathname:{' '}
                                  <code>{window.location.pathname}</code>
                                </p>
                                {window.location.pathname.includes('auth') && (
                                  <div
                                    style={{
                                      backgroundColor: '#fff3cd',
                                      padding: '15px',
                                      margin: '15px 0',
                                      borderRadius: '5px',
                                      border: '1px solid #ffeaa7',
                                    }}
                                  >
                                    <strong>
                                      ⚠️ Authentication URL Detected
                                    </strong>
                                    <p
                                      style={{
                                        margin: '5px 0',
                                        fontSize: '14px',
                                      }}
                                    >
                                      This appears to be an authentication
                                      callback URL. This might indicate a
                                      Firebase OAuth configuration issue.
                                    </p>
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="primary"
                                onClick={() =>
                                  isDesktop()
                                    ? navigate('/')
                                    : startTransition(() => navigate('/'))
                                }
                              >
                                Go Back to Home
                              </Button>
                            </div>
                          }
                        />
                      </Routes>
                    </Suspense>
                  </Container>
                </ErrorBoundary>
              </div>

              {/* Footer */}
              {location.pathname !== '*' && <Footer theme={theme} />}

              {/* Floating AI Chat Component - Show for all users */}
              <FloatingAIChat
                vehicleContext={vehicleContext}
                userMembership={user ? userRole : 'free'}
                chatAccessExpired={user ? !hasChatAccess : false}
                theme={theme}
              />

              {/* Exit-intent popup for free users about to leave */}
              <ExitIntentPopup userMembership={user ? userRole : 'free'} />

              {/* Authentication Modals */}
              <LoginModal
                show={showLoginModal}
                onHide={() => startTransition(() => setShowLoginModal(false))}
                onSuccess={handleLoginSuccess}
                onSwitchToSignUp={handleSwitchToSignUp}
              />

              <SignUpModal
                show={showSignUpModal}
                onHide={() => startTransition(() => setShowSignUpModal(false))}
                onSuccess={handleSignUpSuccess}
                onSwitchToLogin={handleSwitchToLogin}
              />
            </div>
          </DemoRouteGuard>
        </DomainRedirect>
      </SafeRenderProvider>
    </HelmetProvider>
  );
}

export default App;
